/**
 * Registre des credentials (clés de sécurité) requises par type d'étape.
 *
 * Utilisé pour afficher, dans la barre d'outils de l'étape, un bouton
 * (StepCredentialsButton) permettant de consulter/renseigner ces clés
 * sans quitter la configuration du scénario.
 *
 * Structure de chaque entrée : { name: string, label: string }
 *   - name : nom exact de la credential attendu par le worker (saas_back/services/workers.js)
 *   - label : libellé affiché dans la modale
 */

export const STEP_CREDENTIAL_REQUIREMENTS = {
	"firecrawl": [
		{ name: "firecrawl_key", label: "Clé Firecrawl" },
	],

	// ── IA ──────────────────────────────────────────────────────────────────
	// Le service utilisé (ChatGPT ou Perplexity) est choisi dans le formulaire de l'étape,
	// les deux clés sont donc proposées.
	"chatgpt_v2": [
		{ name: "chatgpt_key", label: "Clé ChatGPT (OpenAI)" },
		{ name: "perplexity_key", label: "Clé Perplexity" },
	],
	"chatgpt": [
		{ name: "chatgpt_key", label: "Clé ChatGPT (OpenAI)" },
		{ name: "perplexity_key", label: "Clé Perplexity" },
	],
	"perplexity": [
		{ name: "chatgpt_key", label: "Clé ChatGPT (OpenAI)" },
		{ name: "perplexity_key", label: "Clé Perplexity" },
	],
	"chatgpt_train": [
		{ name: "chatgpt_key", label: "Clé ChatGPT (OpenAI)" },
	],

	// ── Import / Export ──────────────────────────────────────────────────────
	"ftp": [
		{ name: "ftp_host", label: "Hôte FTP" },
		{ name: "ftp_port", label: "Port FTP" },
		{ name: "ftp_user", label: "Utilisateur FTP" },
		{ name: "ftp_pwd", label: "Mot de passe FTP" },
	],

	// ── OCR ──────────────────────────────────────────────────────────────────
	"abbyy": [
		{ name: "abbyy_id", label: "Identifiant Abbyy" },
		{ name: "abbyy_pwd", label: "Mot de passe Abbyy" },
	],

	// ── Traduction (legacy) ───────────────────────────────────────────────────
	// Un seul des deux services (Google ou DeepL) suffit, les deux clés sont proposées.
	"translate": [
		{ name: "google_translate_api_key", label: "Clé API Google Translate" },
		{ name: "deepl_api_key", label: "Clé API DeepL" },
		{ name: "deepl_url", label: "URL API DeepL" },
	],

	// ── Services / Connecteurs ───────────────────────────────────────────────
	"linkedin": [
		{ name: "linkedin_key", label: "Clé Linkedin (Brightdata)" },
	],

	// ── Mail ─────────────────────────────────────────────────────────────────
	// Utilisés uniquement quand l'étape est en mode "credentials" (host/login/pwd non saisis
	// directement dans le formulaire) — cf src/components/steps/mail.vue.
	"mail": [
		{ name: "mail_host", label: "Serveur mail (host)" },
		{ name: "mail_login", label: "Login mail" },
		{ name: "mail_pwd", label: "Mot de passe mail" },
	],
};

/**
 * Correspondance entre un nom de credential et la clé partagée SMD (admin) équivalente,
 * telle qu'activable par un admin sur /admin_client (AdminClient.vue -> client.details.credentials).
 * Permet de signaler, dans la modale des credentials d'une étape, qu'une clé partagée SMD
 * est active pour le client même si aucune credential n'est renseignée ici.
 */
export const ADMIN_SHARED_CREDENTIALS = {
	chatgpt_key: { type: "chatgpt", key: "chatgpt_key" },
	perplexity_key: { type: "chatgpt", key: "perplexity_key" },
	serpapi_key: { type: "api", key: "serpapi_key" },
};

/**
 * Retourne la liste des credentials requises pour une action donnée (insensible à la casse).
 * @param {string} action
 * @returns {Array<{name: string, label: string}>}
 */
export function getStepCredentialRequirements(action) {
	if (!action) return [];
	return STEP_CREDENTIAL_REQUIREMENTS[action.toLowerCase()] || [];
}
